import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  CateringOrder,
  OrderStatus,
  CancellationReason,
  OrderNote,
  CommunicationLogEntry,
} from '../data/types';
import { toOrderItemPayload } from '../utils/lineItemWire';
import type { RootState } from '../../../redux/rootReducer';
import type { ListOrdersFilters } from '../../../redux/catering/cateringTypes';
import Store from '../../../redux/store';
import {
  fetchOrdersRequest,
  createOrderRequest,
  updateOrderRequest,
  orderActionRequest,
  transitionOrderStatusRequest,
} from '../../../redux/catering/cateringActions';

/**
 * Redux-backed React hook with the same call signature as the legacy zustand
 * store. All consumers (`OrdersPage`, `OrderDetailPage`, `OrderFormPage`,
 * `LeadAnalyticsPanel`, etc.) keep working unchanged — every method routes
 * to the v2.2 catering-orders API via redux-saga.
 *
 * v2.2 has no equivalent for equipment, prep-list, duplicate, or per-note
 * endpoints — those methods are kept as no-ops so the existing UI doesn't
 * crash, with `console.warn` so they're easy to find and remove.
 */
interface CateringOrdersHook {
  orders: CateringOrder[];
  loading: boolean;
  page: number;
  total: number;
  totalPages: number;
  fetchOrders: (filters?: ListOrdersFilters) => void;
  addOrder: (order: Omit<CateringOrder, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateOrder: (id: string, updates: Partial<CateringOrder>, force?: boolean) => void;
  removeOrder: (id: string) => void;
  updateStatus: (id: string, status: OrderStatus, changedBy: string) => void;
  cancelOrder: (id: string, reason: CancellationReason, notes: string) => void;
  addNote: (orderId: string, note: Omit<OrderNote, 'id'>) => void;
  duplicateOrder: (id: string) => string | null;
  addCommunicationLogEntry: (orderId: string, entry: Omit<CommunicationLogEntry, 'id'>) => void;
}

// Module-level in-flight flag. Goes true the moment a fetch is dispatched and
// resets when the fetch settles (success or failure → `loading` flips back to
// false). This blocks duplicate dispatches inside the same render tick —
// React.StrictMode's double-mount, or two consumers of this hook mounting
// back-to-back — without blocking re-fetches on later page navigations.
let ordersFetchInFlight = false;

function useCateringOrdersStoreImpl(options?: { skipAutoFetch?: boolean }): CateringOrdersHook {
  const dispatch = useDispatch();
  const orders = useSelector((s: RootState) => s.catering.orders.orders);
  const loading = useSelector((s: RootState) => s.catering.orders.loading);
  const page = useSelector((s: RootState) => s.catering.orders.page);
  const total = useSelector((s: RootState) => s.catering.orders.total);
  const totalPages = useSelector((s: RootState) => s.catering.orders.totalPages);

  useEffect(() => {
    if (options?.skipAutoFetch) return;
    if (ordersFetchInFlight) return;
    ordersFetchInFlight = true;
    dispatch(fetchOrdersRequest({ filters: { includeCancelled: true } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Release the in-flight flag only when loading transitions true → false.
  // Using a ref so the initial render (loading = false) does NOT reset the flag
  // immediately after the fetch was dispatched above.
  const wasLoadingRef = useRef(false);
  useEffect(() => {
    if (loading) {
      wasLoadingRef.current = true;
    } else if (wasLoadingRef.current) {
      ordersFetchInFlight = false;
      wasLoadingRef.current = false;
    }
  }, [loading]);

  const fetchOrders: CateringOrdersHook['fetchOrders'] = useCallback((filters?: ListOrdersFilters) => {
    dispatch(fetchOrdersRequest({ filters }));
  }, [dispatch]);

  const addOrder = useCallback<CateringOrdersHook['addOrder']>((order) => {
    const leadId = order.leadId || undefined;
    const customer = order.customer
      ? {
          firstName: order.customer.firstName,
          lastName: order.customer.lastName,
          phone: order.customer.phone || undefined,
          email: order.customer.email || undefined,
          company: order.customer.company || undefined,
          // Left unset (not '') when blank so the backend's estimate-gstin carry-over applies.
          gstin: order.customer.gstin || undefined,
        }
      : undefined;
    dispatch(
      createOrderRequest({
        data: {
          customer,
          leadId,
          estimateId: order.estimateId ?? undefined,
          orderTypeId: order.orderType,
          headcount: order.headcount,
          eventName: order.eventName,
          eventType: order.eventType ? String(order.eventType) : undefined,
          fulfillment: {
            date: order.fulfillment?.date ?? '',
            time: order.fulfillment?.time,
            dispatchTime: order.fulfillment?.dispatchTime || undefined,
            servingTime: order.fulfillment?.servingTime || undefined,
            address: order.fulfillment?.address,
          },
          // Catalog lines send itemId; custom/addon lines (no itemId) send name +
          // unitPrice, tagged isAddon when applicable. Skip rows that have neither
          // (incomplete UI rows). `sortOrder` reflects array position — the caller
          // (OrderFormPage) is responsible for ordering addons last.
          items: (order.items ?? [])
            .filter((li) => !!li.itemId || (li.itemName && li.itemName.trim()))
            .map(toOrderItemPayload),
          appliedCharges: (order.appliedCharges ?? [])
            .filter((c) => !c.removed)
            .map((c) => ({
              sourceChargeId: c.sourceChargeId || undefined,
              name: c.name,
              type: c.chargeType as 'PERCENT' | 'FLATFEE',
              value: c.chargeValue ?? c.rate,
              amount: c.amount,
              taxable: c.taxable,
            })),
          appliedTaxes: (order.appliedTaxes ?? [])
            .filter((t) => !t.exempt)
            .map((t) => {
              // Send the bare mh_taxfees id (strip the "item:" row-id prefix) — taxfees_id is VARCHAR(36).
              const rawId = t.taxfeesId ?? t.id ?? '';
              const taxfeesId = rawId.startsWith('item:') ? rawId.slice(5) : (rawId || undefined);
              return {
                taxfeesId,
                name: t.name,
                type: t.type,
                value: t.rate,
                amount: t.amount ?? 0,
                isTaxable: t.isTaxable ?? false,
                isItemTax: t.isItemTax ?? false,
                ...(t.itemIds?.length ? { itemIds: t.itemIds } : {}),
              };
            }),
          discount: order.discount
            ? { type: order.discount.type, value: order.discount.value, reason: order.discount.reason }
            : undefined,
          internalNotes: order.internalNotes || undefined,
          instructions: order.fulfillment?.instructions || undefined,
          contractTerms: order.contractTerms || undefined,
          contactPerson: order.fulfillment?.contactPerson || undefined,
          orderChannel: order.orderChannel || undefined,
          status: (order.status === 'draft' ? 'draft' : 'confirmed') as any,
          allowPast: order.allowPast || undefined,
          dietaryRestrictions: order.dietaryRestrictions,
        },
      }),
    );
  }, [dispatch]);

  const updateOrder = useCallback<CateringOrdersHook['updateOrder']>((id, updates, force) => {
    dispatch(
      updateOrderRequest({
        orderId: id,
        force,
        data: {
          customer: updates.customer
            ? {
                firstName: updates.customer.firstName || undefined,
                lastName: updates.customer.lastName || undefined,
                phone: updates.customer.phone || undefined,
                email: updates.customer.email || undefined,
                company: updates.customer.company || undefined,
                gstin: updates.customer.gstin || undefined,
              }
            : undefined,
          orderTypeId: updates.orderType || undefined,
          headcount: updates.headcount,
          eventName: updates.eventName,
          eventType: updates.eventType ? String(updates.eventType) : undefined,
          fulfillment: updates.fulfillment
            ? {
                date: updates.fulfillment.date,
                time: updates.fulfillment.time,
                dispatchTime: updates.fulfillment.dispatchTime || undefined,
                servingTime: updates.fulfillment.servingTime || undefined,
                address: updates.fulfillment.address,
              }
            : undefined,
          items: updates.items
            ?.filter((li) => !!li.itemId || (li.itemName && li.itemName.trim()))
            .map(toOrderItemPayload),
          appliedCharges: updates.appliedCharges
            ? updates.appliedCharges
                .filter((c) => !c.removed)
                .map((c) => ({
                  sourceChargeId: c.sourceChargeId || undefined,
                  name: c.name,
                  type: c.chargeType as 'PERCENT' | 'FLATFEE',
                  value: c.chargeValue ?? c.rate,
                  amount: c.amount,
                  taxable: c.taxable,
                }))
            : undefined,
          appliedTaxes: updates.appliedTaxes
            ? updates.appliedTaxes
                .filter((t) => !t.exempt)
                .map((t) => {
                  // Send the bare mh_taxfees id (strip the "item:" row-id prefix) — taxfees_id is VARCHAR(36).
                  const rawId = t.taxfeesId ?? t.id ?? '';
                  const taxfeesId = rawId.startsWith('item:') ? rawId.slice(5) : (rawId || undefined);
                  return {
                    taxfeesId,
                    name: t.name,
                    type: t.type,
                    value: t.rate,
                    amount: t.amount ?? 0,
                    isTaxable: t.isTaxable ?? false,
                    isItemTax: t.isItemTax ?? false,
                    ...(t.itemIds?.length ? { itemIds: t.itemIds } : {}),
                  };
                })
            : undefined,
          discount: updates.discount
            ? { type: updates.discount.type, value: updates.discount.value, reason: updates.discount.reason }
            : undefined,
          internalNotes: updates.internalNotes || undefined,
          instructions: updates.fulfillment?.instructions,
          contractTerms: updates.contractTerms,
          contactPerson: updates.fulfillment?.contactPerson || undefined,
          status: updates.status === 'draft' || updates.status === 'confirmed' ? updates.status : undefined,
          dietaryRestrictions: updates.dietaryRestrictions,
        },
      }),
    );
  }, [dispatch]);

  const updateStatus = useCallback<CateringOrdersHook['updateStatus']>((id, status) => {
    if (status === 'cancelled') {
      dispatch(
        orderActionRequest({
          orderId: id,
          action: 'cancel',
          reason: 'other',
        }),
      );
      return;
    }
    if (status === 'confirmed' || status === 'in_prep' || status === 'completed') {
      dispatch(transitionOrderStatusRequest({ orderId: id, status }));
      return;
    }
    // 'ready' / 'out_for_delivery' / 'draft' have no equivalent backend transition.
    console.warn(`[useCateringOrdersStore] updateStatus(${status}) — no backend route in v2.2`);
  }, [dispatch]);

  const cancelOrder = useCallback<CateringOrdersHook['cancelOrder']>((id, reason, notes) => {
    dispatch(
      orderActionRequest({
        orderId: id,
        action: 'cancel',
        reason,
        note: notes || undefined,
      }),
    );
  }, [dispatch]);

  // No-op stubs for features removed in v2.2.
  // Keep them so existing UI code paths don't crash.
  const noop = useCallback((label: string) => {
    return (..._args: unknown[]) => {
      console.warn(`[useCateringOrdersStore] ${label} — no backend route in v2.2`);
    };
  }, []);

  const removeOrder = useMemo(() => noop('removeOrder') as CateringOrdersHook['removeOrder'], [noop]);
  const addNote = useMemo(() => noop('addNote') as CateringOrdersHook['addNote'], [noop]);
  const addCommunicationLogEntry = useMemo(
    () => noop('addCommunicationLogEntry') as CateringOrdersHook['addCommunicationLogEntry'],
    [noop],
  );

  const duplicateOrder = useCallback<CateringOrdersHook['duplicateOrder']>(() => {
    console.warn('[useCateringOrdersStore] duplicateOrder — no backend route in v2.2');
    return null;
  }, []);

  return {
    orders,
    loading,
    page,
    total,
    totalPages,
    fetchOrders,
    addOrder,
    updateOrder,
    removeOrder,
    updateStatus,
    cancelOrder,
    addNote,
    duplicateOrder,
    addCommunicationLogEntry,
  };
}

/**
 * Imperative snapshot for callers outside React (other zustand stores etc.).
 * Mirrors the zustand `.getState()` API the legacy store exposed.
 */
function getState(): {
  orders: CateringOrder[];
  addOrder: (order: Omit<CateringOrder, 'id' | 'createdAt' | 'updatedAt'>) => void;
} {
  const orders = (Store.getState() as RootState).catering.orders.orders;
  return {
    orders,
    addOrder(order) {
      Store.dispatch(
        createOrderRequest({
          data: {
            customer: order.customer
              ? {
                  firstName: order.customer.firstName,
                  lastName: order.customer.lastName,
                  phone: order.customer.phone || undefined,
                  email: order.customer.email || undefined,
                  company: order.customer.company || undefined,
                }
              : undefined,
            leadId: order.leadId ?? undefined,
            estimateId: order.estimateId ?? undefined,
            orderTypeId: order.orderType,
            headcount: order.headcount,
            eventName: order.eventName,
            fulfillment: {
              date: order.fulfillment?.date ?? '',
              time: order.fulfillment?.time,
              address: order.fulfillment?.address,
            },
            items: (order.items ?? []).map((li) => ({
              itemId: li.itemId,
              quantity: li.quantity,
              notes: li.notes || undefined,
            })),
            appliedCharges: (order.appliedCharges ?? [])
              .filter((c) => !c.removed)
              .map((c) => ({
                sourceChargeId: c.sourceChargeId || undefined,
                name: c.name,
                type: c.chargeType as 'PERCENT' | 'FLATFEE',
                value: c.chargeValue,
                amount: c.amount,
                taxable: c.taxable,
              })),
            discount: order.discount
              ? { type: order.discount.type, value: order.discount.value, reason: order.discount.reason }
              : undefined,
            instructions: order.fulfillment?.instructions || undefined,
          },
        }),
      );
    },
  };
}

export const useCateringOrdersStore = Object.assign(useCateringOrdersStoreImpl, { getState });
